// PATH: src/pages/SchoolPage.tsx — fiche d'identité de l'établissement :
// mentions légales et coordonnées, saisies par la direction.
//
// Ces mentions alimentent les documents officiels (certificats, reçus). Le NOM
// de l'école n'est pas modifiable ici : il vient du compte tc_identity, partagé
// avec le reste de l'écosystème.
import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, Card, Col, Form, Input, Result, Row, Space, Typography, message } from "antd";
import { PictureOutlined, SaveOutlined } from "@ant-design/icons";
import { useProfileApi, type SchoolProfile } from "../api/profile";
import PageHeader from "../components/PageHeader";
import PhotoUpload from "../components/PhotoUpload";

const SchoolPage: React.FC = () => {
  const api = useProfileApi();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [schoolName, setSchoolName] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    api.get()
      .then((p) => {
        setSchoolName(p.school_name ?? "");
        form.setFieldsValue(p);
      })
      .catch((err) => {
        if (err?.response?.status === 403) setForbidden(true);
        else message.error("Chargement impossible");
      })
      .finally(() => setLoading(false));
  }, [api, form]);
  useEffect(load, [load]);

  const onFinish = (values: Partial<SchoolProfile>) => {
    setSaving(true);
    api.save(values)
      .then((p) => {
        setSchoolName(p.school_name ?? "");
        message.success("Fiche enregistrée");
      })
      .catch((err) => message.error(err?.response?.data?.error || "Enregistrement refusé"))
      .finally(() => setSaving(false));
  };

  if (forbidden) {
    return <Result status="403" title="Fiche réservée à la direction de l'établissement" />;
  }

  return (
    <div>
      <PageHeader
        title="École"
        subtitle={schoolName || "Identité et coordonnées de l'établissement"}
        action={
          <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={() => form.submit()}>
            Enregistrer
          </Button>
        }
      />

      <Form form={form} layout="vertical" onFinish={onFinish} disabled={loading}>
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={12}>
            <Card title="Mentions légales" size="small" loading={loading}>
              <Alert
                type="info" showIcon style={{ marginBottom: 16 }}
                message="Ces identifiants figurent sur les documents officiels de l'établissement. Renseignez-les tels qu'ils apparaissent sur vos pièces administratives."
              />
              <Form.Item
                label="Raison sociale"
                name="legal_name"
                // Le nom d'usage vient de tc_identity : ce champ ne sert qu'au
                // cas où la dénomination légale en diffère.
                help={schoolName ? `Nom d'usage du compte : ${schoolName}. À renseigner uniquement si la dénomination légale diffère.` : undefined}
              >
                <Input placeholder={schoolName || "Dénomination légale"} />
              </Form.Item>
              <Row gutter={12}>
                <Col xs={24} md={12}>
                  <Form.Item label="ICE" name="ice" help="Identifiant Commun de l'Entreprise — 15 chiffres.">
                    <Input placeholder="001234567000089" inputMode="numeric" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label="Identifiant fiscal (IF)" name="tax_id">
                    <Input placeholder="12345678" inputMode="numeric" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label="Registre de commerce (RC)" name="rc_number">
                    <Input placeholder="123456" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  {/* Un RC ne se lit pas sans son tribunal : le backend refuse
                      un numéro sans ville. */}
                  <Form.Item
                    label="Ville du RC" name="rc_city"
                    help="Tribunal émetteur — requis avec le numéro de RC."
                  >
                    <Input placeholder="Casablanca" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label="Patente" name="patente">
                    <Input placeholder="Taxe professionnelle" />
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          </Col>

          <Col xs={24} lg={12}>
            <Card title="Coordonnées" size="small" loading={loading}>
              <Form.Item label="Adresse" name="address">
                <Input.TextArea rows={2} placeholder="12 rue Mohammed V" />
              </Form.Item>
              <Row gutter={12}>
                <Col xs={24} md={14}>
                  <Form.Item label="Ville" name="city">
                    <Input placeholder="Salé" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={10}>
                  <Form.Item label="Code postal" name="postal_code">
                    <Input placeholder="11000" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label="Téléphone" name="phone">
                    <Input placeholder="+212 5 37 00 00 00" inputMode="tel" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label="Email" name="email" rules={[{ type: "email", message: "Email invalide" }]}>
                    <Input placeholder="contact@ecole.ma" inputMode="email" />
                  </Form.Item>
                </Col>
                <Col xs={24}>
                  <Form.Item label="Site web" name="website">
                    <Input placeholder="https://www.ecole.ma" />
                  </Form.Item>
                </Col>
                <Col xs={24}>
                  {/* Le logo s'envoie dès la sélection (relais tc_upload) et son
                      URL renseigne le champ, écrit avec le reste à
                      l'enregistrement. Il coiffe ensuite les documents officiels
                      (certificats, attestations, reçus). valuePropName="value" :
                      PhotoUpload se pilote comme un contrôle antd. */}
                  <Form.Item label="Logo de l'établissement" name="logo_url" valuePropName="value">
                    <PhotoUpload
                      addLabel="Ajouter le logo"
                      changeLabel="Changer le logo"
                      icon={<PictureOutlined />}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>

        <Space style={{ marginTop: 16 }}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Tous les champs sont facultatifs : la fiche se complète au fil du temps.
          </Typography.Text>
        </Space>
      </Form>
    </div>
  );
};

export default SchoolPage;
